import { IntegrationMediaType, type Integration, type IntegrationMutationPayload } from './model';
import {
  normalizeCustomTargeting,
  normalizeLiverampTargeting,
  serializeAdvancedTargeting,
} from '../../targeting/advancedTargetingModel';
import {
  createTargetingFormValue,
  serializeTargetingFormValue,
  type PersistedTargeting,
} from '../../targeting/model';
import { emptyIntegrationFormValue, type IntegrationFormValue } from './integrationFormSchema';
import {
  createIntegrationRewriteSets,
  serializeIntegrationRewriteSets,
} from './integrationRewriteModel';
import { dealSchema } from '../deals/model';
import { TargetingConsumer } from '../../targeting/config';
import { splitLines } from '../../targeting/modelHelpers';

export function integrationToFormValue(integration: Integration): IntegrationFormValue {
  const targeting = integration.targeting as PersistedTargeting | null | undefined;

  return {
    ...emptyIntegrationFormValue,
    demandPartnerId: integration.demandPartner.id,
    name: integration.name,
    status: integration.status,
    integrationType: integration.integrationType,
    mediaTypes: splitMediaTypes(integration.mediaTypes),
    schainAsi: integration.schainAsi ?? '',
    sellerId: integration.sellerId ?? '',
    notes: integration.notes ?? '',
    blockBidsWithoutCat: integration.blockBidsWithoutCat ?? false,
    blockBidsWithoutAdomain: integration.blockBidsWithoutAdomain ?? false,
    multipleImpressionsEnabled: integration.multipleImpressionsEnabled ?? false,
    multipleImpressionsCount: integration.multipleImpressionsCount ?? undefined,
    duplicateDemandIntegration: integration.duplicateDemandIntegration ?? false,
    dupCount: integration.dupCount ?? undefined,
    dupUseDefaultFloor: integration.dupUseDefaultFloor ?? false,
    dupMinFloor: integration.dupMinFloor ?? undefined,
    dupMaxFloor: integration.dupMaxFloor ?? undefined,
    dupIncrement: integration.dupIncrement ?? undefined,
    endpointUri: integration.endpointUri ?? '',
    endpointUriApac: integration.endpointUriApac ?? '',
    endpointUriEu: integration.endpointUriEu ?? '',
    endpointUriUsEast: integration.endpointUriUsEast ?? '',
    endpointUriUsWest: integration.endpointUriUsWest ?? '',
    gzip: integration.gzip ?? false,
    displayManager: integration.displayManager ?? '',
    advertiserId: integration.advertiserId ?? '',
    targetCpm: integration.targetCpm ?? undefined,
    bidFloor: integration.bidFloor ?? undefined,
    qps: integration.qps ?? undefined,
    shareOfVoice: integration.shareOfVoice ?? undefined,
    biddingTmax: integration.biddingTmax ?? undefined,
    publisherId: integration.publisherId ?? '',
    siteId: integration.siteId ?? '',
    appId: integration.appId ?? '',
    placementId: integration.placementId ?? '',
    zoneId: integration.zoneId ?? '',
    tagId: integration.tagId ?? '',
    tripleliftInventoryCode: integration.tripleliftInventoryCode ?? '',
    openxDelDomain: integration.openxDelDomain ?? '',
    unrulyTargetingUuid: integration.unrulyTargetingUuid ?? '',
    openxUnitId: integration.openxUnitId ?? '',
    smartAdServerPageId: integration.smartAdServerPageId ?? '',
    smartAdServerNetworkId: integration.smartAdServerNetworkId ?? '',
    smartAdServerFormatId: integration.smartAdServerFormatId ?? '',
    impExt: integration.impExt ?? '',
    groupId: integration.groupId ?? '',
    bidRequestExtCo: integration.bidRequestExtCo ?? '',
    bidRequestExt: integration.bidRequestExt ?? '',
    integrationRewriteSets: createIntegrationRewriteSets(integration.integrationRewrite),
    listRewrites: integration.listRewrites.map(({ id, name }) => ({
      id,
      ...(name ? { name } : {}),
    })),
    targetingId: targeting?.id ?? '',
    targetingName: targeting?.name ?? '',
    bundlesCount: targeting?.bundlesCount ?? 0,
    ipsCount: targeting?.ipsCount ?? 0,
    pendingBundleCsv: null,
    pendingIpCsv: null,
    targeting: createTargetingFormValue(targeting, TargetingConsumer.DemandConnection),
    customTargeting: normalizeCustomTargeting(
      integration.customTargeting ?? targeting?.customTargeting,
    ),
    liverampTargeting: normalizeLiverampTargeting(
      integration.liverampTargeting ?? targeting?.liverampTargeting,
    ),
    schainLogicMode: integration.schainLogicMode ?? emptyIntegrationFormValue.schainLogicMode,
    incompleteSchainHandling:
      integration.incompleteSchainHandling ?? emptyIntegrationFormValue.incompleteSchainHandling,
    listSchains: integration.listSchains.map(({ id, name, type }) => ({
      id,
      ...(name ? { name } : {}),
      ...(type ? { type } : {}),
    })),
    deals: integration.deals.flatMap((deal) => {
      const id = deal.id ?? deal.dealId ?? deal.initialId;
      if (!id) return [];
      const parsed = dealSchema.safeParse({ ...deal, id });
      return parsed.success ? [parsed.data] : [];
    }),
    pmpEnabled: Boolean(integration.privateAuction || integration.deals.length),
    privateAuction: integration.privateAuction ?? false,
  };
}

function splitMediaTypes(value?: string) {
  const knownMediaTypes = new Set<string>(Object.values(IntegrationMediaType));
  return splitValues(value).filter((item): item is IntegrationMediaType =>
    knownMediaTypes.has(item),
  );
}

export function serializeIntegrationFormValue(
  value: IntegrationFormValue,
  integrationId?: string,
): IntegrationMutationPayload {
  const targeting = serializeIntegrationTargeting(value);
  const customTargeting = serializeAdvancedTargeting(value.customTargeting);
  const liverampTargeting = serializeAdvancedTargeting(value.liverampTargeting);
  return {
    ...(integrationId ? { id: integrationId } : {}),
    name: value.name.trim(),
    status: value.status,
    demandPartner: { id: value.demandPartnerId },
    integrationType: value.integrationType,
    mediaTypes: value.mediaTypes.join(','),
    ...textFields(value),
    multipleImpressionsEnabled: value.multipleImpressionsEnabled,
    multipleImpressionsCount: value.multipleImpressionsEnabled
      ? finiteOrNull(value.multipleImpressionsCount)
      : null,
    duplicateDemandIntegration: value.duplicateDemandIntegration,
    dupCount: value.duplicateDemandIntegration ? finiteOrNull(value.dupCount) : null,
    dupUseDefaultFloor: value.dupUseDefaultFloor,
    ...numberFields(value),
    gzip: value.gzip,
    blockBidsWithoutCat: value.blockBidsWithoutCat,
    blockBidsWithoutAdomain: value.blockBidsWithoutAdomain,
    integrationRewrite: serializeIntegrationRewriteSets(value.integrationRewriteSets),
    listRewrites: value.listRewrites.map(({ id }) => ({ id })),
    listSchains: value.listSchains.map(({ id }) => ({ id })),
    targeting: {
      ...(value.targetingId
        ? {
            id: value.targetingId,
            ...(value.targetingName ? { name: value.targetingName } : {}),
          }
        : {}),
      ...targeting,
      customTargeting,
    },
    customTargeting,
    liverampTargeting,
    schainLogicMode: value.schainLogicMode,
    incompleteSchainHandling: value.incompleteSchainHandling,
    privateAuction: value.pmpEnabled ? value.privateAuction : false,
    deals: value.pmpEnabled ? value.deals.map(({ id }) => ({ id })) : [],
  };
}

function serializeIntegrationTargeting(value: IntegrationFormValue) {
  const shared = serializeTargetingFormValue(value.targeting, TargetingConsumer.DemandConnection);
  return {
    deviceTypes: shared.deviceTypes?.map(({ deviceTypeId, id, excluded }) => ({
      id: deviceTypeId ?? id,
      excluded,
    })),
    platforms: value.targeting.platformIds.map((platform) => ({
      platform,
      excluded: value.targeting.platformsExcluded,
    })),
    geoLocations: shared.geoLocations,
    zipCodes: shared.zipCodes,
    includedApps: shared.includedApps,
    excludedApps: shared.excludedApps,
    domains: splitLines(value.targeting.domainsText).map((id) => ({
      id,
      excluded: !value.targeting.domainsIncluded,
    })),
    includedTailoredSupplies: shared.includedTailoredSupplies,
    excludedTailoredSupplies: shared.excludedTailoredSupplies,
    targetingLRSegments: shared.targetingLRSegments,
    iabCategories: shared.iabCategories,
    includeBundles: shared.includeBundles,
    includeIps: shared.includeIps,
    reqWithoutDeviceIfa: shared.reqWithoutDeviceIfa,
    reqWithoutAppStoreUrl: shared.reqWithoutAppStoreUrl,
    reqFilterOutInstlNotEq1: shared.reqFilterOutInstlNotEq1,
    reqWithoutContentChannel: shared.reqWithoutContentChannel,
    reqWithoutContentNetwork: shared.reqWithoutContentNetwork,
    pmpDealIds: shared.pmpDealIds,
    xp1: shared.xp1,
    xp2: shared.xp2,
    xp3: shared.xp3,
    xp4: shared.xp4,
    xpExcluded: shared.xpExcluded,
  };
}

const textFieldKeys = [
  'schainAsi',
  'sellerId',
  'notes',
  'endpointUri',
  'endpointUriApac',
  'endpointUriEu',
  'endpointUriUsEast',
  'endpointUriUsWest',
  'displayManager',
  'advertiserId',
  'publisherId',
  'siteId',
  'appId',
  'placementId',
  'zoneId',
  'tagId',
  'tripleliftInventoryCode',
  'openxDelDomain',
  'unrulyTargetingUuid',
  'openxUnitId',
  'smartAdServerPageId',
  'smartAdServerNetworkId',
  'smartAdServerFormatId',
  'impExt',
  'groupId',
  'bidRequestExtCo',
  'bidRequestExt',
] as const;

const numberFieldKeys = [
  'dupMinFloor',
  'dupMaxFloor',
  'dupIncrement',
  'targetCpm',
  'bidFloor',
  'qps',
  'shareOfVoice',
  'biddingTmax',
] as const;

function textFields(value: IntegrationFormValue): Record<(typeof textFieldKeys)[number], string> {
  return Object.fromEntries(textFieldKeys.map((key) => [key, value[key].trim()])) as Record<
    (typeof textFieldKeys)[number],
    string
  >;
}

function numberFields(
  value: IntegrationFormValue,
): Record<(typeof numberFieldKeys)[number], number | null> {
  return Object.fromEntries(
    numberFieldKeys.map((key) => [key, finiteOrNull(value[key])]),
  ) as Record<(typeof numberFieldKeys)[number], number | null>;
}

function finiteOrNull(value: number | undefined) {
  return value !== undefined && Number.isFinite(value) ? value : null;
}

function splitValues(value?: string) {
  return (
    value
      ?.split(',')
      .map((item) => item.trim())
      .filter(Boolean) ?? []
  );
}
